import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
// Redux
import { RootState } from "app/store";
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
import { getFloatingSearchPages } from "app/store/DMSDocumentPages/DMSDocumentPages.async";
import { DMSDocumentPagesActions } from "app/store/DMSDocumentPages/DMSDocumentPages.slice";
import { selectDocumentsAsOptions } from "app/store/DMSDocuments/DMSDocuments.selectors";
// Mui
import {
  Box,
  Dialog,
  IconButton,
  Stack,
  Tooltip
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
// 
import DialogSearchResults from "./DialogSearchResults";
import DialogSearchInput from "./DialogSearchInput";
import DialogFilterSelectors from "./DialogFilterSelectors";

interface IProps {
  open: boolean;
  onClose: () => void;
}

export default function CasePageSearchDialog({ open, onClose }: IProps) {
  const { caseId } = useParams<{ caseId: string }>();

  const dispatch = useAppDispatch();

  const documentOptions = useAppSelector((state:RootState) => selectDocumentsAsOptions(state, {
    excludeCustomFormat: false,
    onlyOcred: true
  }));

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [enableFullscreen, setEnableFullscreen] = useState(false);

  const initialDocumentIds = ['all'];
  const methods = useForm({
    defaultValues: {
      keyword: '',
      documentIds: initialDocumentIds
    }
  });

  useEffect(() => {
    return () =>{
      dispatch(DMSDocumentPagesActions.setInitialField('floatingSearchPages'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = methods.handleSubmit(async (data:any) => {
    const { keyword, documentIds } = data;

    if ( !keyword.trim() ) return;

    dispatch(getFloatingSearchPages({
      insuranceCaseId: Number(caseId),
      keywords: [keyword],
      match: 'all',
      documentIds: documentIds[0] === 'all'
        ? documentOptions.map(({ value }) => value)
        : documentIds
    }));
  });

  const handleFullscreenClick = () => {
    setEnableFullscreen(!enableFullscreen);
  }

  const paperSx = !enableFullscreen ? {
    position: 'absolute',
    top: '4%',
    borderRadius: 4
  } : {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={enableFullscreen}
      fullWidth
      maxWidth="lg"
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            flexDirection: "row",
            ...paperSx
          },
        }
      }}
    >
      {/* Main content */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <FormProvider {...methods}>
            <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, p: 4 }}>
              {enableFullscreen ? <Box sx={{ width: 88 }} /> : null}
              <Stack
                sx={{
                  maxWidth: 1140,
                  width: '100%',
                  flexGrow: 1,
                  gap: 4,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 2,
                  mx: 'auto',
                }}
                direction="column"
                component="form"
                onSubmit={onSubmit}
                noValidate
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <DialogSearchInput />
                  <Tooltip title={showFilters ? 'Hide search options' : 'Show search options'} placement="top">
                    <IconButton
                      onClick={() => setShowFilters(!showFilters)}
                      color={showFilters ? 'primary' : 'default'}
                      size="small"
                    ><TuneIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>

                {/* Filters */}
                {showFilters ? (
                  <DialogFilterSelectors initialDocumentIds={initialDocumentIds} />
                ) : null}
              </Stack>
              <Box sx={{ display: 'flex', gap: 2, pt: 1.25 }}>
                <Tooltip title={enableFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} placement="top">
                  <IconButton
                    onClick={handleFullscreenClick}
                    color={enableFullscreen ? 'primary' : 'default'}
                  ><FullscreenIcon /></IconButton>
                </Tooltip>
                <Tooltip title="Close search" placement="top">
                  <IconButton
                    onClick={onClose}
                  ><CloseIcon /></IconButton>
                </Tooltip>
              </Box>
            </Box>
          </FormProvider>
          {/* Results */}
          <DialogSearchResults enableFullscreen={enableFullscreen} />
        </Box>
    </Dialog>
  );
}
